/**
 * Organizer & Shop API types.
 * Backend DTO'larını mirror'lar. Backend'de değişiklik olunca buraları güncelle.
 */

/* ============================================================================
 * i18n + Currency — platform-wide sabitler. Backend SupportedLocales /
 * SupportedCurrencies ile senkron. Dil/currency eklerken iki tarafı da güncelle.
 * ========================================================================= */

export const SUPPORTED_LOCALES = ["tr", "en", "ar"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "tr";

/** RTL (right-to-left) olan diller. */
export const RTL_LOCALES: readonly Locale[] = ["ar"];

export function isRtlLocale(locale: Locale): boolean {
  return RTL_LOCALES.includes(locale);
}

export function getLocaleDir(locale: Locale): "ltr" | "rtl" {
  return isRtlLocale(locale) ? "rtl" : "ltr";
}

export const SUPPORTED_CURRENCIES = ["TRY", "EUR", "USD"] as const;
export type CurrencyCode = (typeof SUPPORTED_CURRENCIES)[number];
export const DEFAULT_CURRENCY: CurrencyCode = "TRY";

export type OrganizerStatus =
  | "PendingEmailVerification"
  | "PendingPhoneVerification"
  | "Active"
  | "Suspended"
  | "Deleted";

export type ShopStatus = "Active" | "Paused" | "Archived";

/**
 * Storefront hero (üst banner) layout preset'i.
 * - `Profile`: kimlik-odaklı, kapak + sol alt avatar + isim.
 * - `Showcase`: görsel-odaklı, yüksek kapak + koyu gradient + üstte büyük isim.
 */
export type ShopHeroStyle = "Profile" | "Showcase";

export interface ShopSummary {
  id: string;
  slug: string;
  name: string;
  status: ShopStatus;
  defaultLocale: Locale;
}

/**
 * Shop detay — /shops ve /shops/{slug} endpoint'lerinin dönüş tipi.
 * Currency burada yok: her TicketType kendi currency'sini tutar (shop currency-agnostic).
 */
export interface Shop {
  id: string;
  organizerId: string;
  slug: string;
  name: string;
  description: string | null;
  logoUrl: string | null;
  coverImageUrl: string | null;
  themeColor: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  status: ShopStatus;
  defaultLocale: Locale;
  heroStyle: ShopHeroStyle;
  createdAt: string;
  updatedAt: string;
}

export interface CreateShopRequest {
  name: string;
  description?: string | null;
  slug?: string | null;
  themeColor?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  /** Storefront default dili. Boşsa organizer'ın PreferredLocale'i. */
  defaultLocale?: Locale | null;
  /** Storefront hero layout. Null gelirse backend "Profile" default'u uygular. */
  heroStyle?: ShopHeroStyle | null;
}

export interface UpdateShopRequest {
  name: string;
  description?: string | null;
  logoUrl?: string | null;
  coverImageUrl?: string | null;
  themeColor?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  defaultLocale?: Locale | null;
  /** Storefront hero layout. Null gelirse backend mevcut değeri korur. */
  heroStyle?: ShopHeroStyle | null;
}

export interface SignupRequest {
  email: string;
  password: string;
  companyName: string;
  phoneNumber: string;
  /** UI'nın açıldığı dil. Boşsa backend Accept-Language'dan alır. */
  preferredLocale?: Locale | null;
}

export interface SignupResponse {
  organizerId: string;
  email: string;
  companyName: string;
  status: OrganizerStatus;
  defaultShopId: string;
  defaultShopSlug: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface VerifyEmailRequest {
  token: string;
}

export interface ResendVerificationRequest {
  email: string;
}

/**
 * Organizer self-service profile update.
 * Logo + yasal bilgiler (firma unvanı, vergi, adres). Yasal alanların eksiksiz
 * olması Shop'un Active'e alınabilmesi için zorunlu (backend guard).
 */
export interface UpdateOrganizerProfileRequest {
  /** Boş/null → mevcut logo kaldırılır. */
  logoUrl?: string | null;
  /** Resmi firma unvanı (ticari ünvan). KVKK veri sorumlusu olarak gözükür. */
  legalName?: string | null;
  /** TCKN (11) veya VKN (10). Backend salt sayı + uzunluk validasyonu yapar. */
  taxNumber?: string | null;
  /** Vergi dairesi. Mesafeli satış sözleşmesinde taraf bilgisi. */
  taxOffice?: string | null;
  /** Fatura / yasal yazışma adresi. */
  billingAddress?: string | null;
}

export interface OrganizerSessionResponse {
  organizerId: string;
  email: string;
  companyName: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  preferredLocale: Locale;
  /** Platform'un bu organizer'a uyguladığı komisyon oranı (%). Read-only. */
  commissionRate: number;
  /** Bilet başına sabit servis ücreti. Read-only. */
  serviceFee: number;
  /** Organizer logosu — dashboard header + storefront'ta görünür. */
  logoUrl: string | null;
  /** Resmi firma unvanı. Yasal sayfalarda veri sorumlusu olarak kullanılır. */
  legalName: string | null;
  taxNumber: string | null;
  taxOffice: string | null;
  billingAddress: string | null;
  /**
   * Tüm yasal alanlar (legalName + taxNumber + taxOffice + billingAddress) dolu mu?
   * Backend hesaplar; false ise shop Active'e alınamaz.
   */
  isLegalInfoComplete: boolean;
  shops: ShopSummary[];
}

// ---------- Uploads ----------

export type UploadKind =
  | "EventCover"
  | "ShopLogo"
  | "ShopCover"
  | "OrganizerLogo";

export interface UploadResponse {
  /** Public, CDN'e servis edilen mutlak URL. DB'ye bu yazılır. */
  url: string;
  /** Bucket içindeki tam key (prefix dahil). İleride delete için saklanabilir. */
  key: string;
}

// ---------- Event ----------

export type EventStatus =
  | "Draft"
  | "Published"
  | "Paused"
  | "Cancelled"
  | "Ended"
  | "Archived";

export interface TicketType {
  id: string;
  eventId: string;
  name: string;
  description: string | null;
  price: number;
  /** Her ticket kendi currency'sinde fiyatlanır. Shop currency-agnostic. */
  currency: CurrencyCode;
  quantity: number;
  sold: number;
  available: number;
  /** Satış penceresi — null ise event publish anından itibaren satışa açık. */
  salesStart: string | null;
  salesEnd: string | null;
  /** Sepet başına adet sınırları. */
  minPerOrder: number;
  maxPerOrder: number | null;
  /** KDV oranı (%) — null ise event.defaultTaxRate fallback. */
  taxRate: number | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface Event {
  id: string;
  organizerId: string;
  slug: string;
  title: string;
  description: string | null;
  coverImageUrl: string | null;
  venueName: string | null;
  venueAddress: string | null;
  venueCity: string | null;
  /** WGS84 enlem. Dashboard'daki harita picker set eder. */
  latitude: number | null;
  /** WGS84 boylam. */
  longitude: number | null;
  startsAt: string;
  endsAt: string;
  /** Event default KDV oranı (%). TicketType.taxRate null ise bu kullanılır. */
  defaultTaxRate: number;
  status: EventStatus;
  publishedAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  ticketTypes: TicketType[];
  createdAt: string;
  updatedAt: string;
}

export interface EventSummary {
  id: string;
  slug: string;
  title: string;
  coverImageUrl: string | null;
  venueCity: string | null;
  startsAt: string;
  endsAt: string;
  status: EventStatus;
  ticketTypeCount: number;
  totalCapacity: number;
  totalSold: number;
}

export interface CreateEventRequest {
  title: string;
  description?: string | null;
  coverImageUrl?: string | null;
  slug?: string | null;
  venueName?: string | null;
  venueAddress?: string | null;
  venueCity?: string | null;
  /** WGS84 enlem/boylam. Dashboard haritadan seçince set edilir. */
  latitude?: number | null;
  longitude?: number | null;
  startsAt: string;
  endsAt: string;
  /** Event default KDV oranı (%). 0-100 arası. Default 20. */
  defaultTaxRate?: number;
}

export interface UpdateEventRequest {
  title: string;
  description?: string | null;
  coverImageUrl?: string | null;
  venueName?: string | null;
  venueAddress?: string | null;
  venueCity?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  startsAt: string;
  endsAt: string;
  defaultTaxRate?: number;
}

export interface CancelEventRequest {
  reason?: string | null;
}

export interface CreateTicketTypeRequest {
  name: string;
  description?: string | null;
  price: number;
  currency: CurrencyCode;
  quantity: number;
  /** Satış penceresi — ISO 8601 UTC. Null/undefined → event publish anından itibaren satışta. */
  salesStart?: string | null;
  salesEnd?: string | null;
  /** Sepet başına min/max adet. */
  minPerOrder?: number;
  maxPerOrder?: number | null;
  /** KDV oranı (%) — null → event.defaultTaxRate fallback. */
  taxRate?: number | null;
  sortOrder?: number;
}

export interface UpdateTicketTypeRequest {
  name: string;
  description?: string | null;
  price: number;
  currency: CurrencyCode;
  quantity: number;
  salesStart?: string | null;
  salesEnd?: string | null;
  minPerOrder?: number;
  maxPerOrder?: number | null;
  taxRate?: number | null;
  sortOrder?: number;
}

// ---------- ShopEvent (Shop ↔ Event join) ----------

export type ShopEventStatus = "Listed" | "Hidden";

/**
 * Bir ShopEvent altında satılmak üzere seçilen TicketType'ın özet bilgisi.
 * Dashboard "bu mağazada hangi biletler satılıyor" listesi için.
 */
export interface ShopEventTicketInfo {
  ticketTypeId: string;
  name: string;
  price: number;
  currency: CurrencyCode;
  quantity: number;
  sold: number;
}

export interface ShopEvent {
  id: string;
  shopId: string;
  eventId: string;
  status: ShopEventStatus;
  section: string | null;
  displayOrder: number;
  eventSlug: string;
  eventTitle: string;
  eventCoverImageUrl: string | null;
  eventVenueCity: string | null;
  eventStartsAt: string;
  eventEndsAt: string;
  eventStatus: EventStatus;
  /** Bu shop'ta bu event için satılacak ticket type listesi. */
  attachedTickets: ShopEventTicketInfo[];
  createdAt: string;
  updatedAt: string;
}

export interface AttachEventToShopRequest {
  eventId: string;
  /** Bu shop'ta satılacak ticket type'lar. En az 1 zorunlu. */
  ticketTypeIds: string[];
  section?: string | null;
  displayOrder?: number;
}

export interface UpdateShopEventRequest {
  status: ShopEventStatus;
  section?: string | null;
  displayOrder: number;
}

export interface UpdateShopEventTicketsRequest {
  ticketTypeIds: string[];
}

// ---------- Public (storefront) ----------

export interface PublicShop {
  slug: string;
  name: string;
  description: string | null;
  logoUrl: string | null;
  coverImageUrl: string | null;
  themeColor: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  defaultLocale: Locale;
  heroStyle: ShopHeroStyle;
}

/**
 * Event kartı fiyat aralığı + currency. Shop currency-agnostic olduğu için
 * her event kartı kendi currency'sini taşır.
 */
export interface PublicPriceRange {
  min: number;
  max: number;
  currency: CurrencyCode;
}

export interface PublicEventListItem {
  slug: string;
  title: string;
  coverImageUrl: string | null;
  venueName: string | null;
  venueCity: string | null;
  startsAt: string;
  endsAt: string;
  /** Fiyat aralığı + currency. Null → ticket yok veya quantity 0. */
  priceRange: PublicPriceRange | null;
  section: string | null;
  displayOrder: number;
  isSoldOut: boolean;
}

export interface PublicTicketType {
  id: string;
  name: string;
  description: string | null;
  /** Taban fiyat — breakdown için ham değer. Storefront ana gösterimi `totalPrice`. */
  price: number;
  /** Alıcıya gösterilen brüt fiyat: base + KDV + servis. "KDV & servis dahil". */
  totalPrice: number;
  /** Bilet başına sabit servis ücreti (breakdown'da gösterilir). */
  serviceFee: number;
  /** Uygulanan KDV oranı (%). */
  taxRate: number;
  /** Her ticket kendi currency'sinde. */
  currency: CurrencyCode;
  available: number;
  isSoldOut: boolean;
  salesStart: string | null;
  salesEnd: string | null;
  minPerOrder: number;
  maxPerOrder: number | null;
  /** Bu bilet şu an satışta mı (window + event status). */
  isOnSale: boolean;
  sortOrder: number;
}

export interface PublicEventDetail {
  slug: string;
  title: string;
  description: string | null;
  coverImageUrl: string | null;
  venueName: string | null;
  venueAddress: string | null;
  venueCity: string | null;
  /** Storefront küçük harita + yol tarifi linki için. */
  latitude: number | null;
  longitude: number | null;
  startsAt: string;
  endsAt: string;
  /** En az bir ticket satışta mı. */
  isOnSale: boolean;
  isSoldOut: boolean;
  ticketTypes: PublicTicketType[];
  shop: PublicShop;
  /**
   * Aktif otomatik promosyonlar (code=null). Storefront event detayında badge olarak gösterilir.
   * Kodlu kuponlar burada değildir — kullanıcı checkout'ta kodu girdiğinde devreye girer.
   */
  activePromotions: PublicActivePromotion[];
}

/**
 * Yasal sayfa slug sabitleri — backend LegalPageSlugs sınıfıyla senkron.
 * URL'de ve storefront footer'ında bu string'ler kullanılır.
 */
export const LEGAL_PAGE_SLUGS = [
  "kvkk",
  "gizlilik",
  "kullanim-kosullari",
  "mesafeli-satis",
  "iade-iptal",
] as const;
export type LegalPageSlug = (typeof LEGAL_PAGE_SLUGS)[number];

/**
 * Storefront yasal sayfa render yanıtı. Backend template'i organizer
 * bilgileriyle doldurmuş veya override varsa onu geri vermiş olur.
 */
export interface PublicLegalPage {
  slug: string;
  title: string;
  /** Render edilmiş HTML — storefront doğrudan dangerouslySetInnerHTML ile basar. */
  contentHtml: string;
  /** Organizer override'ı devrede mi (sayfa platform şablonu değil mi). */
  isOverridden: boolean;
  /** ISO UTC timestamp. Override varsa override.updatedAt, yoksa template generation time. */
  lastUpdatedAt: string;
}

/**
 * Dashboard override editörü için detay yanıtı. Platform şablonunun ham Markdown'ı
 * ("eski değer"e dönüş için referans) + organizer override'ı (varsa).
 */
export interface LegalOverrideDetail {
  slug: string;
  title: string;
  /** Platform template'inin Markdown kaynağı — editör "default" görünümü için. */
  defaultTemplateMarkdown: string;
  /** Organizer override Markdown'ı; override yoksa null. */
  overrideMarkdown: string | null;
  /** Override varsa son güncelleme timestamp'i. */
  overrideUpdatedAt: string | null;
}

/**
 * Override kaydet/güncelle payload'ı. Boş string → override silinir, template'e döner.
 * İçerik Markdown; backend render sırasında sanitize eder.
 */
export interface UpsertLegalOverrideRequest {
  contentMarkdown: string;
}

/** Storefront event detayında gösterilecek otomatik kampanya özeti. */
export interface PublicActivePromotion {
  id: string;
  name: string;
  description: string | null;
  type: PromotionTypeValue;
  buyX: number | null;
  getY: number | null;
  discountValue: number | null;
  discountCurrency: CurrencyCode | null;
  endsAt: string | null;
  /** null → event'in tüm biletleri kapsam içinde. Doluysa sadece bu id'ler. */
  ticketTypeIds: string[] | null;
}

// ---------- Orders ----------

export type OrderStatus = "Pending" | "Paid" | "Cancelled" | "Expired" | "Refunded";
export type TicketStatusValue = "Valid" | "Used" | "Cancelled";

export interface OrderItemRequestDto {
  ticketTypeId: string;
  quantity: number;
}

export interface CreateOrderRequest {
  shopSlug: string;
  eventSlug: string;
  items: OrderItemRequestDto[];
  buyerEmail: string;
  buyerFirstName: string;
  buyerLastName: string;
  buyerPhone: string;
  /** Opsiyonel kupon kodu. Boşsa otomatik promolar uygulanır. */
  couponCode?: string | null;
}

/** Preview: sepet fiyat kırılımı — sipariş oluşturmaz, stok düşürmez. */
export interface PreviewOrderRequest {
  shopSlug: string;
  eventSlug: string;
  items: OrderItemRequestDto[];
  couponCode?: string | null;
}

export interface AppliedPromotionResponse {
  promotionId: string;
  name: string;
  type: PromotionTypeValue;
  codeUsed: string | null;
  discountAmount: number;
  freeQuantity: number;
}

export interface PreviewOrderResponse {
  currency: string;
  subtotal: number;
  taxTotal: number;
  serviceFeeTotal: number;
  discountTotal: number;
  freeQuantityTotal: number;
  total: number;
  items: OrderItemResponse[];
  appliedPromotion: AppliedPromotionResponse | null;
  /** Kullanıcı kod girdi ama uygulanmadı — UI "kod geçersiz" uyarısı gösterir. */
  couponInvalid: boolean;
}

export interface OrderItemResponse {
  id: string;
  ticketTypeId: string;
  ticketTypeName: string;
  quantity: number;
  /** Taban fiyat per unit (KDV ve servis hariç). Catalog snapshot, indirim öncesi. */
  unitPrice: number;
  /** Per unit effective KDV tutarı (indirim sonrası base üzerinden). */
  taxAmount: number;
  /** Per unit servis ücreti. IsFree=true ise 0. */
  serviceFee: number;
  /** Uygulanan KDV oranı (%). */
  taxRate: number;
  /** Line base total effective = unitPrice*quantity - discountAmount. */
  subtotal: number;
  /** BOGO'dan gelen bedava QR satırı mı? True ise tüm fiyat alanları 0. */
  isFree: boolean;
  /** Bu satırı doğuran/etkileyen promosyon. Null → promo yok. */
  promotionId: string | null;
  /** Satıra düşen indirim tutarı (Percentage/FixedAmount). BOGO için 0. */
  discountAmount: number;
}

export interface OrderTicketResponse {
  id: string;
  ticketCode: string;
  ticketTypeName: string;
  status: TicketStatusValue;
}

/**
 * POST /public/orders response. Pending durumunda, biletler henüz yok.
 */
export interface PendingOrderResponse {
  orderNumber: string;
  status: OrderStatus;
  total: number;
  currency: string;
  expiresAt: string;
  items: OrderItemResponse[];
}

/**
 * Pay sonrası veya success sayfasında gösterilen detay.
 */
export interface OrderDetailResponse {
  orderNumber: string;
  status: OrderStatus;
  buyerEmail: string;
  buyerFirstName: string;
  buyerLastName: string;
  /** Taban fiyat toplamı (indirim sonrası, KDV + servis hariç). */
  subtotal: number;
  /** Toplam KDV. */
  taxTotal: number;
  /** Toplam servis ücreti. */
  serviceFeeTotal: number;
  /** Promosyonlardan toplam indirim (transparency için). */
  discountTotal: number;
  /** BOGO'dan gelen toplam bedava QR sayısı. */
  freeQuantityTotal: number;
  /** @deprecated Legacy alias — serviceFeeTotal ile aynı. */
  serviceFee: number;
  /** Alıcının ödediği toplam = subtotal + taxTotal + serviceFeeTotal. */
  total: number;
  currency: string;
  paidAt: string | null;
  createdAt: string;
  eventTitle: string;
  eventSlug: string;
  eventVenueName: string | null;
  eventStartsAt: string;
  shopSlug: string;
  shopName: string;
  items: OrderItemResponse[];
  tickets: OrderTicketResponse[];
  appliedPromotions: AppliedPromotionResponse[];
  /**
   * Son başarısız ödeme denemesinin hata nedeni (ör. "Yetersiz bakiye").
   * Pending status'te kullanıcıya "banka dedi ki" olarak gösterilir; Paid'e
   * geçince yine dolu olabilir (tarihsel iz).
   */
  lastPaymentError?: string | null;
  /** Checkout'ta toplanan alıcı telefonu — SMS bildirimi için. */
  buyerPhone: string;
}

// ---------- Payments ----------

/**
 * Sunucudaki PaymentProviderKey enum'ı (string serialization).
 * Backend "Param" / "Isbank" / "Halkbank" döner.
 */
export type PaymentProviderKey = "Param" | "Isbank" | "Halkbank" | "Dev";

/**
 * Storefront kart formundan toplanan veri. ASLA log'lanmamalı, localStorage'a
 * yazılmamalı. Bir kez form submit'inde backend'e gidip bellekten silinir.
 */
export interface CardInfoRequest {
  holderName: string;
  /** Sadece rakamlar — boşluklar client tarafında temizlenebilir, backend de temizliyor. */
  number: string;
  /** "MM" — 1-12, backend 2 haneye pad ediyor. */
  expMonth: string;
  /** "YY" veya "YYYY" — backend 4 haneye normalize ediyor. */
  expYear: string;
  cvc: string;
}

export interface InitPaymentRequest {
  providerKey: PaymentProviderKey;
  /** 1 = tek çekim. MVP'de her zaman 1. */
  installmentCount: number;
  card: CardInfoRequest;
}

export interface InitPaymentResponse {
  orderNumber: string;
  /** 3D Secure hosted sayfanın URL'i. Browser'ı buraya yönlendir. */
  redirectUrl: string;
  /** Alıcının fiilen ödeyeceği tutar (taksit farkı dahil). */
  buyerTotal: number;
  currency: string;
}

// ---------- Promotions ----------

export type PromotionTypeValue = "BuyXGetY" | "Percentage" | "FixedAmount";

export interface PromotionTicketTypeSummary {
  id: string;
  name: string;
}

export interface Promotion {
  id: string;
  name: string;
  description: string | null;
  /** Opsiyonel kupon kodu (organizer-scope unique). Boş → otomatik. */
  code: string | null;
  type: PromotionTypeValue;
  /** BuyXGetY: ücretli adet eşiği. */
  buyX: number | null;
  /** BuyXGetY: bedava eklenen adet. */
  getY: number | null;
  /** Percentage: 0-100, FixedAmount: tutar. */
  discountValue: number | null;
  /** FixedAmount currency. */
  discountCurrency: CurrencyCode | null;
  startsAt: string | null;
  endsAt: string | null;
  /** null → organizatörün tüm etkinlikleri. */
  eventId: string | null;
  eventTitle: string | null;
  /** Boş → event'in tüm ticket türleri. */
  ticketTypes: PromotionTicketTypeSummary[];
  maxUses: number | null;
  usageCount: number;
  priority: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePromotionRequest {
  name: string;
  description?: string | null;
  code?: string | null;
  type: PromotionTypeValue;
  buyX?: number | null;
  getY?: number | null;
  discountValue?: number | null;
  discountCurrency?: CurrencyCode | null;
  startsAt?: string | null;
  endsAt?: string | null;
  eventId?: string | null;
  ticketTypeIds?: string[] | null;
  maxUses?: number | null;
  priority: number;
  isActive: boolean;
}

export type UpdatePromotionRequest = CreatePromotionRequest;

export interface PromotionRedemption {
  id: string;
  promotionId: string;
  orderId: string;
  orderNumber: string;
  codeUsed: string | null;
  discountAmount: number;
  freeQuantity: number;
  redeemedAt: string;
  isReversed: boolean;
  reversedAt: string | null;
}

/**
 * Organizer dashboard'da satışları listeleme.
 */
export interface OrganizerOrderListItem {
  orderNumber: string;
  status: OrderStatus;
  buyerEmail: string;
  buyerFirstName: string;
  buyerLastName: string;
  buyerPhone: string;
  total: number;
  currency: string;
  ticketCount: number;
  eventTitle: string;
  eventSlug: string;
  shopSlug: string;
  createdAt: string;
  paidAt: string | null;
}

/**
 * GET /orders response — liste + sayfalama meta bilgisi.
 * `hasMore` bir sonraki sayfanın var olup olmadığını belirtir ("Daha fazla yükle").
 */
export interface OrganizerOrderListResponse {
  items: OrganizerOrderListItem[];
  page: number;
  pageSize: number;
  totalCount: number;
  hasMore: boolean;
}

/** Dashboard orders list query parametreleri. */
export interface OrganizerOrderListQuery {
  search?: string;
  status?: OrderStatus;
  page?: number;
  pageSize?: number;
}

export interface CancelOrderRequest {
  reason?: string;
}

/**
 * Backend'den gelen RFC 7807 ProblemDetails formatı.
 */
export interface ApiProblem {
  type?: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  errors?: Record<string, string[]>;
}

/* ============================================================================
 * i18n helpers — lokalize formatting utilities.
 * ========================================================================= */

/**
 * Intl.NumberFormat ile currency formatla. `amount` major unit (örn. 125.50 TRY).
 *
 * TR formatlaması: "125,50 ₺". EN: "$125.50". AR: RTL-aware.
 */
export function formatCurrency(
  amount: number,
  currency: CurrencyCode,
  locale: Locale = DEFAULT_LOCALE,
): string {
  return new Intl.NumberFormat(localeToBcp47(locale), {
    style: "currency",
    currency,
    // Tam sayılar "125 ₺" olarak çıksın, kuruşlu "125,50 ₺"
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Lokalize tarih/saat formatlama. Default Istanbul timezone'unda basılır;
 * ileride shop.timezone eklenirse oradan alınır.
 */
export function formatDateTime(
  value: string | Date,
  locale: Locale = DEFAULT_LOCALE,
  options: Intl.DateTimeFormatOptions = {
    dateStyle: "medium",
    timeStyle: "short",
  },
): string {
  const date = typeof value === "string" ? new Date(value) : value;
  return new Intl.DateTimeFormat(localeToBcp47(locale), {
    timeZone: "Europe/Istanbul",
    ...options,
  }).format(date);
}

/** "tr" → "tr-TR", "en" → "en-US", "ar" → "ar". Intl API'lar region-aware. */
function localeToBcp47(locale: Locale): string {
  switch (locale) {
    case "tr":
      return "tr-TR";
    case "en":
      return "en-US";
    case "ar":
      return "ar";
  }
}

/* ============================================================================
 * Pricing helpers — backend `PricingCalculator` ile simetrik. Storefront'ta
 * ön-izleme / breakdown için kullanılır (dashboard form preview dahil).
 * Aritmetik aynı: round-half-to-even, 2 ondalık.
 * ========================================================================= */

/** 2 basamağa round, banker's rounding (round-half-to-even). */
export function roundMoney(value: number): number {
  const scaled = value * 100;
  const floor = Math.floor(scaled);
  const diff = scaled - floor;
  let rounded: number;
  if (diff > 0.5) rounded = floor + 1;
  else if (diff < 0.5) rounded = floor;
  else rounded = floor % 2 === 0 ? floor : floor + 1;
  return rounded / 100;
}

export interface PriceBreakdown {
  basePrice: number;
  taxAmount: number;
  serviceFee: number;
  commission: number;
  unitGross: number;
  lineGross: number;
  lineBase: number;
  lineTax: number;
  lineServiceFee: number;
  lineCommission: number;
}

/**
 * Per-unit brüt fiyat — alıcının ödediği rakam.
 *   gross = base + (base * taxRate/100) + serviceFee
 * Storefront ticket card / detay / event listesinde bu gösterilir.
 */
export function calculateGross(
  basePrice: number,
  taxRatePercent: number,
  serviceFee: number,
): number {
  const tax = roundMoney((basePrice * taxRatePercent) / 100);
  return roundMoney(basePrice + tax + serviceFee);
}

/**
 * Dashboard ticket form'unda canlı fiyat ön-izleme için tam breakdown.
 * Organizer "base 100, KDV 20%, fee 5" girdiğinde "Alıcı öder: 125" göstermek için.
 */
export function calculatePriceBreakdown(
  basePrice: number,
  taxRatePercent: number,
  serviceFee: number,
  commissionRatePercent: number,
  quantity: number = 1,
): PriceBreakdown {
  const taxAmount = roundMoney((basePrice * taxRatePercent) / 100);
  const commission = roundMoney((basePrice * commissionRatePercent) / 100);
  const unitGross = roundMoney(basePrice + taxAmount + serviceFee);
  return {
    basePrice,
    taxAmount,
    serviceFee,
    commission,
    unitGross,
    lineGross: roundMoney(unitGross * quantity),
    lineBase: roundMoney(basePrice * quantity),
    lineTax: roundMoney(taxAmount * quantity),
    lineServiceFee: roundMoney(serviceFee * quantity),
    lineCommission: roundMoney(commission * quantity),
  };
}
